import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { X, Save } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import BasicInfoSection from "./form-sections/BasicInfoSection";
import BiographySection from "./form-sections/BiographySection";
import ExperienceRateSection from "./form-sections/ExperienceRateSection";
import SpecializationsSection from "./form-sections/SpecializationsSection";
import BookingLinkSection from "./form-sections/BookingLinkSection";
import LinksSection from "./form-sections/LinksSection";
import DisplaySettingsSection from "./form-sections/DisplaySettingsSection";
import { PublishedExpert } from "@/types/admin";

interface ExpertProfileEditFormProps {
  expertId: string;
  expertData: PublishedExpert;
  onClose: () => void;
  onSave: () => void;
}

export default function ExpertProfileEditForm({
  expertId,
  expertData,
  onClose,
  onSave
}: ExpertProfileEditFormProps) {
  const [formData, setFormData] = useState({
    full_name: expertData.full_name || '',
    professional_title: expertData.professional_title || '',
    bio: expertData.bio || '',
    short_description: expertData.short_description || '',
    years_experience: expertData.years_experience || 0,
    hourly_rate: expertData.hourly_rate || 0,
    specializations: expertData.specializations || [],
    linkedin_url: expertData.linkedin_url || '',
    website_url: expertData.website_url || '',
    google_calendar_link: expertData.google_calendar_link || '',
    is_featured: expertData.is_featured || false,
    display_order: expertData.display_order || 0
  });
  
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (field: string, value: string | number | boolean | string[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      // console.log("🔄 Updating expert profile...");
      
      const { error } = await supabase
        .from("consultation_experts")
        .update({
          full_name: formData.full_name,
          professional_title: formData.professional_title,
          bio: formData.bio,
          short_description: formData.short_description,
          years_experience: formData.years_experience,
          hourly_rate: formData.hourly_rate,
          specializations: formData.specializations,
          linkedin_url: formData.linkedin_url || null,
          website_url: formData.website_url || null,
          google_calendar_link: formData.google_calendar_link || null,
          is_featured: formData.is_featured,
          display_order: formData.display_order
        })
        .eq("id", expertId);

      if (error) throw error;

      // console.log("✅ Expert profile updated successfully");
      toast.success("Expert profile updated successfully");
      onSave();
      onClose();
    } catch (error) {
      // console.error("💥 Error updating expert profile:", error);
      toast.error("Failed to update expert profile");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Edit Expert Profile</CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Basic Information */}
        <BasicInfoSection formData={formData} onInputChange={handleInputChange} />

        {/* Biography */}
        <BiographySection formData={formData} onInputChange={handleInputChange} />

        {/* Experience and Rate */}
        <ExperienceRateSection formData={formData} onInputChange={handleInputChange} />

        {/* Specializations */}
        <SpecializationsSection formData={formData} onInputChange={handleInputChange} />

        {/* Booking Link - Prominently Featured */}
        <BookingLinkSection formData={formData} onInputChange={handleInputChange} />

        {/* Other Links */}
        <LinksSection formData={formData} onInputChange={handleInputChange} />

        {/* Display Settings */}
        <DisplaySettingsSection formData={formData} onInputChange={handleInputChange} />

        {/* Action Buttons */}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            <Save className="h-4 w-4 mr-2" />
            {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
